import React, { useContext, useMemo } from "react";
import { UserRoleType } from "./types/admin/User";
import { useTranslation } from "react-i18next";
import { Section } from "mta-rkvr-styles";
import CurrentUserContext from "./CurrentUserContext";
import { Link } from "react-router-dom";
import {
  ADMINISTRATION,
  ASSETS,
  BALANCE,
  BULKEDIT,
  CONTRACTS,
  ENERGYEFFICIENCY,
  INVESTMENT_PROJECT_BULKEDIT,
  INVESTMENT_PROJECT_SEARCH,
  OPENDATA,
  PROCEEDINGS,
  REALESTATEUNIT,
  REPORTS,
  SUMMARY,
  THI_MANAGEMENT,
  UNIT_MANAGEMENT,
  UNIT_MANAGEMENT_BULKEDIT,
  VAT_MANAGEMENT,
} from "./util/paths";
import {
  canRoleOpenAdminNotifications,
  canRoleOpenAdminThiAndVat,
  canRoleOpenBulkEdit,
  canRoleOpenOrgs,
  canRoleOpenStartProceeding,
  canRoleOpenStartRealEstateUnit,
  canRoleOpenUsers,
} from "./util/rightsManagers/sidebarItems/viewRightsManager";
import { canRoleView as canRoleViewAdministration } from "./util/rightsManagers/admin/viewRightsManager";
import { canRoleOpenContractsWorkSpaces } from "./util/rightsManagers/sidebarItems/viewRightsManager";

const Sitemap: React.FC<{
  loggedIn: boolean;
}> = ({ loggedIn }) => {
  const { t } = useTranslation();

  const { usageRights } = useContext(CurrentUserContext);

  function getLink(link: string, text: string, firstLevel?: boolean) {
    return (
      <div className={!firstLevel ? "pl-4" : ""}>
        <Link to={link}>{text}</Link>
      </div>
    );
  }

  const getAssetItems = (types: UserRoleType[], sitelinks: JSX.Element[]) => {
    if (loggedIn) {
      sitelinks.push(getLink(ASSETS, t("sitemap.assets"), true));
      sitelinks.push(getLink(ASSETS, t("sitemap.assetSearch")));
      sitelinks.push(getLink(ASSETS + "/laiendatud", t("sitemap.assetExtendedSearch")));
      sitelinks.push(getLink(ASSETS + "/osised", t("sitemap.assetPartSearch")));
    } else {
      sitelinks.push(getLink(ASSETS, t("sitemap.assets"), true));
    }
    if (canRoleOpenBulkEdit(types)) {
      sitelinks.push(getLink(ASSETS + BULKEDIT, t("sitemap.assetMassEdit")));
    }
  };

  const getProceedingItems = (types: UserRoleType[], sitelinks: JSX.Element[]) => {
    if (loggedIn) {
      sitelinks.push(getLink(PROCEEDINGS, t("sitemap.proceedings"), true));
      sitelinks.push(getLink(PROCEEDINGS, t("sitemap.proceedingsSearch")));
      sitelinks.push(getLink(PROCEEDINGS + "/laiendatud", t("sitemap.proceedingsExtendedSearch")));
      sitelinks.push(getLink(PROCEEDINGS + "/varad", t("sitemap.proceedingsAssetSearch")));
    } else {
      sitelinks.push(getLink(PROCEEDINGS, t("sitemap.proceedings"), true));
    }

    if (canRoleOpenStartProceeding(types)) {
      sitelinks.push(getLink(PROCEEDINGS + "/alusta", t("sitemap.proceedingStart")));
    }
  };
  const getRealEstateUnitItems = (types: UserRoleType[], sitelinks: JSX.Element[]) => {
    if (canRoleOpenStartRealEstateUnit(types)) {
      sitelinks.push(getLink(REALESTATEUNIT, t("sitemap.realEstateUnits"), true));
      sitelinks.push(getLink(REALESTATEUNIT, t("sitemap.realEstateUnitSearch")));
      sitelinks.push(getLink(REALESTATEUNIT + "/alusta-kvu", t("sitemap.realEstateUnitStart")));
      sitelinks.push(getLink(REALESTATEUNIT + "/alusta-pkvu", t("sitemap.realEstateUnitStartPlanned")));
      sitelinks.push(getLink(REALESTATEUNIT + BULKEDIT, t("sitemap.realEstateUnitBulkEdit")));
      sitelinks.push(getLink(REALESTATEUNIT + UNIT_MANAGEMENT_BULKEDIT, t("sitemap.realEstateUnitManagementBulkEdit")));
      sitelinks.push(getLink(REALESTATEUNIT + INVESTMENT_PROJECT_SEARCH, t("sitemap.realEstateUnitInvestmentSearch")));
      sitelinks.push(
        getLink(REALESTATEUNIT + INVESTMENT_PROJECT_BULKEDIT, t("sitemap.realEstateUnitInvestmentBulkEdit")),
      );
    }
  };

  const getContractItems = (types: UserRoleType[], sitelinks: JSX.Element[]) => {
    sitelinks.push(getLink(CONTRACTS, t("sitemap.contracts"), true));
    if (loggedIn) {
      sitelinks.push(getLink(CONTRACTS, t("sitemap.contractSearch")));
      sitelinks.push(getLink(CONTRACTS + "/laiendatud", t("sitemap.contractExtendedSearch")));
      sitelinks.push(getLink(CONTRACTS + "/varad", t("sitemap.contractAssetSearch")));
      if (canRoleOpenContractsWorkSpaces(types)) {
        sitelinks.push(getLink(CONTRACTS + "/valim", t("sitemap.contractManagement")));
      }
    }
  };

  const getReportItems = (isLoggedIn: boolean, sitelinks: JSX.Element[]) => {
    sitelinks.push(getLink(REPORTS, t("sitemap.reports"), true));

    if (isLoggedIn) {
      sitelinks.push(getLink(REPORTS + BALANCE, t("sitemap.reportsBalance")));
      sitelinks.push(getLink(REPORTS + SUMMARY, t("sitemap.reportsBalanceSummary")));
    }
    sitelinks.push(getLink(REPORTS + OPENDATA, t("sitemap.reportsOpendata")));
    sitelinks.push(getLink(REPORTS + ENERGYEFFICIENCY, t("sitemap.reportsEnergy")));
  };

  const getAdministrationItems = (types: UserRoleType[], sitelinks: JSX.Element[]) => {
    if (!canRoleViewAdministration(types)) {
      return;
    }
    sitelinks.push(getLink(ADMINISTRATION, t("sitemap.administration"), true));

    if (canRoleOpenOrgs(types)) {
      sitelinks.push(getLink(ADMINISTRATION + "/organisatsioonid", t("sitemap.administrationOrgs")));
    }

    if (canRoleOpenUsers(types)) {
      sitelinks.push(getLink(ADMINISTRATION + "/kasutajad", t("sitemap.administrationUsers")));
    }

    if (canRoleOpenAdminNotifications(types)) {
      sitelinks.push(getLink(ADMINISTRATION + "/teated", t("sitemap.administrationNotifications")));
      sitelinks.push(getLink(ADMINISTRATION + "/loendite-haldus", t("sitemap.administrationLists")));
    }

    if (canRoleOpenAdminThiAndVat(types)) {
      sitelinks.push(getLink(ADMINISTRATION + VAT_MANAGEMENT, t("sitemap.administrationVat")));
      sitelinks.push(getLink(ADMINISTRATION + THI_MANAGEMENT, t("sitemap.administrationThi")));
      sitelinks.push(getLink(ADMINISTRATION + UNIT_MANAGEMENT, t("sitemap.administrationUnitManagement")));
    }
  };

  const getNotificationsSubscriptionsItems = (loggedIn: boolean, sitelinks: JSX.Element[]) => {
    if (loggedIn) {
      sitelinks.push(getLink("teavituste-tellimine", t("sitemap.notificationsSubscriptions"), true));
    }
  };

  const links = useMemo(() => {
    const sitelinks: JSX.Element[] = [];
    sitelinks.push(getLink("/", t("sitemap.homePage"), true));

    const types = usageRights;
    getAssetItems(types, sitelinks);
    getProceedingItems(types, sitelinks);
    getContractItems(types, sitelinks);
    getReportItems(loggedIn, sitelinks);
    getRealEstateUnitItems(types, sitelinks);
    getAdministrationItems(types, sitelinks);
    getNotificationsSubscriptionsItems(loggedIn, sitelinks);

    return sitelinks;
  }, [loggedIn, usageRights]);

  return (
    <>
      <Section title={t("sitemap.title")}>{links}</Section>
    </>
  );
};

export default Sitemap;
